import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ProgressStepsCompact } from '@/components/ui/progress-steps';
import { Plus, Printer, Trash2, Mail, MessageCircle, Edit, ArrowRight, CheckCircle, AlertTriangle, PackageCheck, PencilLine } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime, formatDate } from '@/utils/dateFormat';
import { useAuth } from '@/contexts/AuthContext';
import { useFuncionarios, FuncionarioSelect } from '@/components/FuncionarioSelect';
import { EditarQuantidadesDialog } from '@/components/EditarQuantidadesDialog';
import { printItensTermico } from '@/lib/printItensTermico';

export default function RecepcaoPage() {
  const [recepcoes, setRecepcoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRecepcao, setEditingRecepcao] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [recepcaoToDelete, setRecepcaoToDelete] = useState(null);
  const [clienteBloqueado, setClienteBloqueado] = useState(null);
  const [editQtdRecepcao, setEditQtdRecepcao] = useState(null);
  const { user } = useAuth();
  const { funcionarios } = useFuncionarios();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    cliente_id: '',
    itens: [],
    observacoes: '',
    funcionario_id: '',
    funcionario_nome: ''
  });
  const [novoItem, setNovoItem] = useState({
    material_id: '',
    quantidade: 1,
    observacao: ''
  });

  const handleClienteChange = async (clienteId) => {
    setFormData(prev => ({ ...prev, cliente_id: clienteId }));
    setClienteBloqueado(null);
    
    if (!clienteId) return;
    
    try {
      const res = await api.get(`/contratos/cliente/${clienteId}/status`);
      if (res.data && !res.data.pode_receber) {
        setClienteBloqueado(res.data);
      }
    } catch (error) {
      // Se não conseguir verificar, permite continuar (fail-open)
    }
  };

  // Converter materiais para o formato do SearchableSelect
  const materiaisOptions = materiais.map(m => ({
    id: m.id,
    label: m.nome,
    sublabel: `Código: ${m.codigo}`
  }));

  // Converter clientes para o formato do SearchableSelect
  const clientesOptions = clientes.map(c => ({
    id: c.id,
    label: c.nome,
    sublabel: `${c.cnpj_cpf || ''} ${c.telefone ? `| ${c.telefone}` : ''}`
  }));

  // Send recepcao to montagem
  const handleEnviarMontagem = async (recepcaoId) => {
    try {
      const response = await api.post(`/recepcoes/${recepcaoId}/enviar-montagem`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar para montagem');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recepcoesRes, clientesRes, materiaisRes] = await Promise.all([
        api.get('/recepcoes'),
        api.get('/clientes'),
        api.get('/materiais')
      ]);
      setRecepcoes(recepcoesRes.data.filter(r => r.status !== 'em_montagem'));
      setClientes(clientesRes.data);
      setMateriais(materiaisRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    if (clienteBloqueado) {
      toast.error('Cliente bloqueado. Não é possível adicionar itens.');
      return;
    }
    if (!novoItem.material_id || novoItem.quantidade <= 0) {
      toast.error('Selecione um material e quantidade válida');
      return;
    }
    setFormData({
      ...formData,
      itens: [...formData.itens, novoItem]
    });
    setNovoItem({ material_id: '', quantidade: 1, observacao: '' });
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      itens: formData.itens.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.itens.length === 0) {
      toast.error('Adicione pelo menos um item');
      return;
    }
    if (!formData.funcionario_id) {
      toast.error('Selecione o funcionário responsável');
      return;
    }
    try {
      const data = {
        ...formData,
        responsavel_id: user.id
      };
      
      if (editingRecepcao) {
        // Update existing
        await api.put(`/recepcoes/${editingRecepcao.id}`, data);
        toast.success('Recepção atualizada com sucesso!');
      } else {
        // Create new - check notification status
        const response = await api.post('/recepcoes', data);
        const notif = response.data?.notificacoes;
        
        toast.success('Recepção registrada com sucesso!');
        
        if (notif) {
          if (notif.whatsapp_enviado) {
            toast.success('✅ WhatsApp enviado para o cliente');
          } else if (notif.whatsapp_erro) {
            toast.warning(`⚠️ WhatsApp não enviado: ${notif.whatsapp_erro}`);
          }
          if (notif.email_enviado) {
            toast.success('✅ Email enviado para o cliente');
          }
        }
      }
      
      setIsDialogOpen(false);
      setEditingRecepcao(null);
      resetForm();
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Contrato inativo/vencido. Oriente o cliente a procurar a administração para regularização.', {
          duration: 8000,
          style: { background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B' }
        });
      } else {
        toast.error(editingRecepcao ? 'Erro ao atualizar recepção' : 'Erro ao criar recepção');
      }
    }
  };

  const handleSendEmail = async (recepcaoId) => {
    try {
      await api.post(`/recepcoes/${recepcaoId}/enviar-email`);
      toast.success('Comprovante enviado por email!');
    } catch (error) {
      toast.error('Erro ao enviar email');
    }
  };

  const handleSendWhatsApp = async (recepcaoId) => {
    try {
      const response = await api.get(`/recepcoes/${recepcaoId}`);
      const recepcao = response.data.recepcao;
      const cliente = response.data.cliente;
      
      // Format WhatsApp message
      const message = `*COMPROVANTE DE RECEPÇÃO - CME*%0A%0ANúmero: ${recepcao.numero_recepcao}%0ACliente: ${cliente.nome}%0AData: ${formatDateTime(recepcao.data_hora)}%0A%0AVisualize o comprovante completo em: ${window.location.origin}/recepcoes/${recepcaoId}`;
      
      // Open WhatsApp
      const phone = cliente.telefone.replace(/\D/g, '');
      window.open(`https://wa.me/55${phone}?text=${message}`, '_blank');
      
      toast.success('WhatsApp aberto!');
    } catch (error) {
      toast.error('Erro ao abrir WhatsApp');
    }
  };

  const resetForm = () => {
    setEditingRecepcao(null);
    setClienteBloqueado(null);
    setFormData({
      cliente_id: '',
      itens: [],
      observacoes: '',
      funcionario_id: '',
      funcionario_nome: ''
    });
    setNovoItem({ material_id: '', quantidade: 1, observacao: '' });
  };

  const handleEdit = (recepcao) => {
    setEditingRecepcao(recepcao);
    setFormData({
      cliente_id: recepcao.cliente_id,
      itens: recepcao.itens,
      observacoes: recepcao.observacoes || '',
      funcionario_id: recepcao.funcionario_id || '',
      funcionario_nome: recepcao.funcionario_nome || ''
    });
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (recepcao) => {
    setRecepcaoToDelete(recepcao);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recepcaoToDelete) return;
    
    try {
      await api.delete(`/recepcoes/${recepcaoToDelete.id}`);
      toast.success('Recepção excluída com sucesso!');
      setDeleteConfirmOpen(false);
      setRecepcaoToDelete(null);
      fetchData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao excluir recepção';
      toast.error(errorMessage);
    }
  };

  const canDelete = () => {
    // Check if user has permission to delete
    const userRole = user?.perfil || user?.role || 'operador';
    return ['admin', 'enfermeiro', 'gestor'].includes(userRole);
  };

  const getMaterialNome = (id) => {
    if (id === 'diverso') return '📦 Materiais Diversos';
    return materiais.find(m => m.id === id)?.nome || 'N/A';
  };
  const getClienteNome = (id) => clientes.find(c => c.id === id)?.nome || 'N/A';

  const handlePrintItensTermico = (recepcao) => {
    const ok = printItensTermico({
      numero: recepcao.numero_recepcao,
      clienteNome: getClienteNome(recepcao.cliente_id),
      data: recepcao.data_hora,
      itens: recepcao.itens,
      getMaterialNome: (id) => id === 'diverso' ? 'Materiais Diversos' : getMaterialNome(id),
      funcionarioNome: recepcao.funcionario_nome,
      observacoes: recepcao.observacoes
    });
    if (!ok) toast.error('Permita pop-ups no navegador para imprimir');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div data-testid="recepcao-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Recepção de Materiais
          </h1>
          <p className="text-lg text-slate-600 mt-2">Área Suja - Registro de entrada</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="btn-add-recepcao"
              onClick={() => resetForm()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Recepção
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => {
            if (e.target.closest('#searchable-select-portal') || document.getElementById('searchable-select-portal')?.contains(e.target)) {
              e.preventDefault();
            }
          }} onInteractOutside={(e) => {
            if (document.getElementById('searchable-select-portal')) {
              e.preventDefault();
            }
          }}>
            <DialogHeader>
              <DialogTitle>{editingRecepcao ? 'Editar Recepção' : 'Nova Recepção de Materiais'}</DialogTitle>
              {editingRecepcao && (
                <p className="text-sm text-blue-600">Editando: {editingRecepcao.numero_recepcao}</p>
              )}
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="cliente_id">Cliente</Label>
                <SearchableSelect
                  options={clientesOptions}
                  value={formData.cliente_id}
                  onChange={handleClienteChange}
                  placeholder="Selecione o cliente..."
                  searchPlaceholder="Buscar por nome, CPF/CNPJ ou telefone..."
                  emptyMessage="Nenhum cliente encontrado"
                />
                {clienteBloqueado && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2" data-testid="alerta-cliente-bloqueado">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <div>
                      <p className="text-sm font-semibold text-red-800">Contrato {clienteBloqueado.status === 'inativo' ? 'Inativo' : 'Vencido'}</p>
                      <p className="text-sm text-red-700">{clienteBloqueado.motivo}</p>
                      {clienteBloqueado.numero_contrato && (
                        <p className="text-xs text-red-500 mt-1">Contrato: {clienteBloqueado.numero_contrato}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Funcionário Responsável */}
              <div>
                <FuncionarioSelect
                  value={formData.funcionario_id}
                  onChange={(id, nome) => setFormData(prev => ({ ...prev, funcionario_id: id, funcionario_nome: nome }))}
                  funcionarios={funcionarios}
                />
              </div>

              <div className="border-t border-slate-200 pt-4">
                <h3 className="font-semibold mb-3">Adicionar Itens</h3>
                
                {/* Busca com dropdown navegável */}
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-6">
                    <Label>Material</Label>
                    <SearchableSelect
                      options={materiaisOptions}
                      value={novoItem.material_id}
                      onChange={(value) => setNovoItem(prev => ({ ...prev, material_id: value }))}
                      placeholder="Selecione o material..."
                      searchPlaceholder="Buscar por nome ou código..."
                      emptyMessage="Nenhum material encontrado"
                      prependOption={{
                        id: 'diverso',
                        label: 'Materiais Diversos',
                        sublabel: 'Para itens não cadastrados',
                        icon: '📦'
                      }}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Qtd</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={novoItem.quantidade}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setNovoItem(prev => ({ ...prev, quantidade: parseInt(val) || 1 }));
                      }}
                      onFocus={(e) => e.target.select()}
                      onClick={(e) => e.target.select()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddItem();
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-3">
                    <Label>Obs</Label>
                    <Input
                      value={novoItem.observacao}
                      onChange={(e) => setNovoItem(prev => ({ ...prev, observacao: e.target.value }))}
                      placeholder="Opcional"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddItem();
                        }
                      }}
                    />
                  </div>
                  <div className="col-span-1 flex items-end">
                    <Button type="button" onClick={handleAddItem} size="sm" data-testid="btn-add-item">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {formData.itens.length > 0 && (
                <div className="border border-slate-200 rounded-md p-4">
                  <h4 className="font-semibold mb-2">Itens adicionados ({formData.itens.length})</h4>
                  <div className="space-y-2">
                    {formData.itens.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div className="flex-1">
                          <span className="font-medium">{getMaterialNome(item.material_id)}</span>
                          <span className="text-sm text-slate-600 ml-2">x{item.quantidade}</span>
                          {item.observacao && (
                            <span className="text-xs text-slate-500 ml-2">({item.observacao})</span>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(idx)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="observacoes">Observações Gerais</Label>
                <textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  rows="3"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button data-testid="btn-submit" type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={!!clienteBloqueado}>
                  {editingRecepcao ? 'Atualizar Recepção' : 'Registrar Recepção'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Número</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cliente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Itens</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Data/Hora</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Progresso</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {recepcoes.map((recepcao) => (
              <tr key={recepcao.id} data-testid={`recepcao-row-${recepcao.id}`} className="hover:bg-slate-50">
                <td className="px-6 py-4 text-sm font-medium text-slate-900">{recepcao.numero_recepcao}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{getClienteNome(recepcao.cliente_id)}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{recepcao.itens.length}</td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  {formatDateTime(recepcao.data_hora)}
                </td>
                <td className="px-6 py-4 text-sm">
                  <ProgressStepsCompact currentStatus={recepcao.status === 'processado' ? 'processado' : 'pendente'} />
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex gap-2">
                    {recepcao.status !== 'em_montagem' && recepcao.status !== 'processado' && (
                      <Button
                        data-testid={`btn-enviar-montagem-${recepcao.id}`}
                        size="sm"
                        onClick={() => handleEnviarMontagem(recepcao.id)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        title="Enviar para Montagem"
                      >
                        <PackageCheck className="w-4 h-4 mr-1" />
                        Enviar p/ Montagem
                      </Button>
                    )}
                    {recepcao.status === 'em_montagem' && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <PackageCheck className="w-3 h-3 mr-1" />
                        Em Montagem
                      </span>
                    )}
                    <Button
                      data-testid={`btn-edit-${recepcao.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(recepcao)}
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-print-${recepcao.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handlePrintItensTermico(recepcao)}
                      title="Imprimir cupom (térmica 80mm)"
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    {recepcao.status !== 'processado' && (
                      <Button
                        data-testid={`btn-editar-qtd-${recepcao.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => setEditQtdRecepcao(recepcao)}
                        className="text-blue-600"
                        title="Editar quantidades"
                      >
                        <PencilLine className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      data-testid={`btn-email-${recepcao.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendEmail(recepcao.id)}
                      title="Enviar por Email"
                    >
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-whatsapp-${recepcao.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendWhatsApp(recepcao.id)}
                      className="text-green-600 hover:text-green-700"
                      title="Enviar por WhatsApp"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                    {canDelete() && (
                      <Button
                        data-testid={`btn-delete-${recepcao.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteClick(recepcao)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Excluir recepção"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {recepcoes.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            Nenhuma recepção registrada
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Exclusão
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Tem certeza que deseja excluir a recepção{' '}
              <strong>{recepcaoToDelete?.numero_recepcao}</strong>?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Esta ação não pode ser desfeita. A recepção e todos os seus itens serão removidos permanentemente.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-confirm-delete"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditarQuantidadesDialog
        open={!!editQtdRecepcao}
        onClose={() => setEditQtdRecepcao(null)}
        titulo={editQtdRecepcao ? `${editQtdRecepcao.numero_recepcao} — ${getClienteNome(editQtdRecepcao.cliente_id)}` : ''}
        itens={editQtdRecepcao?.itens}
        endpoint={`/recepcoes/${editQtdRecepcao?.id}/quantidades`}
        getMaterialNome={getMaterialNome}
        onSaved={() => fetchData()}
      />
    </div>
  );
}